import { TColorScheme } from "../colors.types";

export const getAntDarkTheme = (colors: TColorScheme) => {
  return {
    hashed: false,
    token: {
      colorPrimary: colors.primary[500],
      colorSuccess: colors.primary[500],
      colorError: colors.error[500],
      colorWarning: colors.warning[500],
      fontFamily: "Inter",
      borderRadius: 6,
    },
    components: {
      Layout: {
        headerBg: "transparent",
        bodyBg: colors.neutral[1200],
        siderBg: colors.neutral[1100],
        triggerBg: "transparent",
        triggerColor: colors.neutral[100],
      },
      Menu: {
        popupBg: colors.neutral[1100],
        margin: 8,
        itemMarginInline: 0,
        subMenuItemBg: "transparent",
        itemColor: colors.white,
      },
      Popover: {
        colorBgElevated: colors.neutral[1100],
      },
      Input: {
        colorText: colors.neutral[100],
        colorBgContainer: colors.neutral[1100],
        colorTextPlaceholder: colors.neutral[500],
        colorBorder: colors.neutral[900],
        controlHeight: 44,
        controlHeightLG: 54,
        controlHeightSM: 36,
        hoverBorderColor: colors.neutral[700],
        activeBorderColor: colors.neutral[600],
        colorPrimaryActive: colors.neutral[400],
        colorPrimaryHover: colors.neutral[500],
        activeShadow: "0 0 0 2px rgba(169, 185, 198, 0.1)",
      },
      InputNumber: {
        colorText: colors.neutral[100],
        colorBgContainer: colors.neutral[1100],
        colorTextPlaceholder: colors.neutral[500],
        colorBorder: colors.neutral[700],
        controlHeight: 44,
        controlHeightLG: 54,
        controlHeightSM: 36,
        hoverBorderColor: colors.neutral[400],
        activeBorderColor: colors.neutral[500],
        colorPrimaryActive: colors.neutral[400],
        colorPrimaryHover: colors.neutral[500],
        activeShadow: "0 0 0 2px rgba(169, 185, 198, 0.1)",
      },
      Radio: {
        colorBgContainer: colors.neutral[1100],
        colorBorder: colors.neutral[900],
        colorPrimary: colors.neutral[900],
        colorPrimaryHover: colors.neutral[800],
        buttonSolidCheckedColor: colors.white,
        buttonSolidCheckedHoverBg: colors.neutral[800],
        controlHeight: 44,
        controlHeightSM: 36,
      },
      Form: {
        labelHeight: 36,
      },
      Card: {
        colorBgContainer: colors.neutral[1100],
        colorBorderSecondary: "transparent",
        borderRadiusLG: 16,
        paddingLG: 20,
        headerHeight: 76,
      },
      Modal: {
        contentBg: colors.neutral[1100],
        footerBg: colors.neutral[1100],
        headerBg: colors.neutral[1100],
      },
      Select: {
        colorBgContainer: colors.neutral[1100],
        colorBgElevated: colors.neutral[1100],
        colorText: colors.neutral[400],
        colorFillTertiary: colors.neutral[1100],
        colorBorder: colors.neutral[700],
        colorTextPlaceholder: colors.neutral[500],
        colorTextQuaternary: colors.neutral[100],
        controlHeight: 44,
        controlHeightLG: 54,
        controlHeightSM: 36,
        borderRadius: 8,
      },
      DatePicker: {
        colorBgContainer: colors.neutral[1100],
        colorBgElevated: colors.neutral[1100],
        colorBorder: colors.neutral[700],
        colorText: colors.neutral[400],
        colorFillTertiary: colors.neutral[1100],
        colorTextPlaceholder: colors.neutral[500],
        colorTextDisabled: colors.neutral[100],
        controlHeight: 44,
        controlHeightLG: 54,
        controlHeightSM: 36,
      },
      Checkbox: {
        colorBorder: colors.neutral[800],
        colorBgContainer: colors.neutral[1100],
        colorWhite: colors.neutral[1100],
        colorPrimary: colors.primary[100],
        colorPrimaryHover: colors.neutral[800],
      },
      Button: {
        colorBgContainer: colors.neutral[1100],
        contentFontSizeSM: 14,
        onlyIconSize: 20,
        onlyIconSizeSM: 16,
        onlyIconSizeLG: 24,
        paddingInlineSM: 16,
        paddingInlineLG: 32,
        paddingInline: 24,
        controlHeightSM: 36,
        controlHeight: 44,
        controlHeightLG: 56,
        fontSize: 14,
        lineWidth: 1,
        marginXS: 8,
        borderRadiusSM: 6,
        borderRadius: 8,
        borderRadiusLG: 10,
        colorBorder: colors.neutral[900],
        defaultColor: colors.white,
        colorLink: colors.primary[400],
        colorLinkActive: colors.primary[500],
        colorLinkHover: colors.primary[300],
        colorBgSolid: colors.neutral[1000],
        colorBgSolidActive: colors.neutral[1100],
        colorBgSolidHover: colors.neutral[900],
      },
      Table: {
        headerBg: "transparent",
        colorBgContainer: colors.neutral[1100],
        colorSplit: "rgba(255, 255, 255, 0.05)",
        cellFontSize: 14,
        cellFontSizeMD: 16,
        cellFontSizeSM: 14,
        cellPaddingBlock: 16,
        cellPaddingInline: 20,
        fontSize: 14,
        fontSizeIcon: 12,
        fontSizeSM: 12,
        lineWidth: 1,
        marginXXS: 4,
        paddingXXS: 4,
        headerBorderRadius: 12,
        fontWeightStrong: 600,
        opacityLoading: 0.65,
        selectionColumnWidth: 48,
      },
      Pagination: {
        colorBgContainer: colors.neutral[1100],
        controlHeightSM: 36,
      },
      Tooltip: {
        colorBgSpotlight: colors.neutral[1100],
        colorTextLightSolid: colors.neutral[200],
        borderRadius: 8,
        sizePopupArrow: 20,
        paddingSM: 24,
        paddingXS: 20,
      },
      Dropdown: {
        colorBgElevated: colors.neutral[1200],
        boxShadowSecondary: `0px 0px 0px 1px ${colors.neutral[800]}`,
      },
      Tabs: {
        itemColor: colors.neutral[600],
        inkBarColor: colors.neutral[100],
        itemActiveColor: colors.neutral[900],
        itemHoverColor: colors.neutral[700],
        itemSelectedColor: colors.neutral[100],
      },
      Result: {
        titleFontSize: 20,
        lineHeightHeading3: 1.4,
      },
      Switch: {
        innerMinMargin: 7,
        innerMaxMargin: 22,
      },
      Carousel: {
        colorBgContainer: colors.neutral[200],
      },
      Typography: {
        colorLink: colors.primary[500],
        colorLinkActive: colors.primary[600],
        colorLinkHover: colors.primary[400],
      },
      Segmented: {
        controlHeight: 44,
        controlHeightSM: 36,
        borderRadius: 12,
        borderRadiusSM: 8,
        trackPadding: 4,
        trackBg: colors.neutral[1000],
        itemSelectedBg: colors.neutral[1100],
      },
    },
  };
};
